import React from "react";

import type { Activity, Region } from "@packages/client";

export type DeploymentFailureReasons = "resources" | "configuration";

export const onboardingTypes = {
  default: "",
  demo: "demo",
  migrate: "migrate",
  scratch: "scratch",
  kickstart: "kickstart"
} as const;

export type OnboardingTypes =
  (typeof onboardingTypes)[keyof typeof onboardingTypes];

export const errorTypes = {
  default: "",
  retry: "retry",
  deployment: "deployment",
  redirect: "redirect",
  custom: "custom",
  provisioningFailure: "provisioningFailure"
} as const;

export type ErrorTypes = (typeof errorTypes)[keyof typeof errorTypes];

export const projectCreationStates = {
  default: "",
  isBeingCreated: "is_being_created",
  isDeploying: "is_deploying",
  isWaitingForPush: "is_waiting_for_push",
  finished: "finished"
} as const;

export type ProjectCreationStates =
  (typeof projectCreationStates)[keyof typeof projectCreationStates];

export const verificationTypes = {
  default: "unset",
  none: "none",
  full: "full",
  ticket: "ticket",
  creditCard: "credit-card",
  idle: "idle"
} as const;

export type VerificationTypes =
  (typeof verificationTypes)[keyof typeof verificationTypes];

export type SetupStepperContextType = {
  setupStepperValues: {
    onboardingType: OnboardingTypes;
    organization: {
      label: string;
      value: string;
      name: string;
    };
    projectName: string;
    productionEnvironment: string;
    region: Region;
    repository?: {
      label: string;
      value: string;
      defaultBranch: string;
    };
    installationId?: string;
    githubOrganizationLabel?: string;
  };
  newProjectValues: {
    errorType?: ErrorTypes;
    errorDetail?: string;
    projectId?: string;
    environmentId?: string;
    projectUrl?: string;
    publicUrl?: string;
    logUrl?: string;
    state: ProjectCreationStates;
  };
  activities: Activity[];
  verificationType: VerificationTypes;
  activeStep: number;
  isCreateProjectLoading: boolean;
  isOnTrial: boolean;
  isLastStep: boolean;
  handleGoForward: () => void;
  handleGoBackward: () => void;
  handleUpdateSetupStepperValues: (newValues: Record<string, any>) => void;
  handleCreateProject: (options?: {
    shouldSkipVerifyCheck?: boolean;
    newValues?: Record<string, any>;
    isRetry?: boolean;
    isOnTrial?: boolean;
  }) => void;
  checkSubscriptionStatus: (params: {
    projectId: string;
    organizationId: string;
    onboardingType: OnboardingTypes;
  }) => void;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  getPreselectedOrganization: () => Partial<Record<string, string>>;
  credentials: Partial<{
    client_secret: string;
    public_key: string;
    automatic_payment_methods: boolean;
  }>;
};

export type GithubOrganization = {
  gh_installation_id: string;
  gh_organization_name: string;
  organization_id: string;
  id: string;
  created_at: string;
  updated_at: string;
};

export type FormatedGithubOrganization = {
  value: string;
  label: string;
};

export type FormatedGithubRepository = {
  value: string;
  label: string;
  defaultBranch: string;
};

export interface GithubRepositories {
  total_count: number;
  repositories: GithubRepository[];
}

export interface GithubBranch {
  commit: { sha: string; url: string };
  name: string;
  protected: boolean;
}

export interface GithubRepository {
  id: number;
  node_id: string;
  owner: Owner;
  name: string;
  full_name: string;
  default_branch: DefaultBranch;
  created_at: string;
  pushed_at: string;
  updated_at: string;
  html_url: string;
  clone_url: string;
  git_url: string;
  ssh_url: string;
  svn_url: string;
  language?: string;
  fork: boolean;
  forks_count: number;
  open_issues_count: number;
  open_issues: number;
  stargazers_count: number;
  watchers_count: number;
  watchers: number;
  size: number;
  permissions: Permissions;
  allow_forking: boolean;
  web_commit_signoff_required: boolean;
  archived: boolean;
  disabled: boolean;
  private: boolean;
  has_issues: boolean;
  has_wiki: boolean;
  has_pages: boolean;
  has_projects: boolean;
  has_downloads: boolean;
  has_discussions: boolean;
  is_template: boolean;
  url: string;
  archive_url: string;
  assignees_url: string;
  blobs_url: string;
  branches_url: string;
  collaborators_url: string;
  comments_url: string;
  commits_url: string;
  compare_url: string;
  contents_url: string;
  contributors_url: string;
  deployments_url: string;
  downloads_url: string;
  events_url: string;
  forks_url: string;
  git_commits_url: string;
  git_refs_url: string;
  git_tags_url: string;
  hooks_url: string;
  issue_comment_url: string;
  issue_events_url: string;
  issues_url: string;
  keys_url: string;
  labels_url: string;
  languages_url: string;
  merges_url: string;
  milestones_url: string;
  notifications_url: string;
  pulls_url: string;
  releases_url: string;
  stargazers_url: string;
  statuses_url: string;
  subscribers_url: string;
  subscription_url: string;
  tags_url: string;
  trees_url: string;
  teams_url: string;
  visibility: Visibility;
  homepage?: string;
  description?: string;
  license?: License;
  topics?: string[];
}

export enum DefaultBranch {
  Main = "main",
  Master = "master"
}

export interface License {
  key: string;
  name: string;
  url: string;
  spdx_id: string;
}

export interface Owner {
  login: string;
  id: number;
  node_id: string;
  avatar_url: string;
  html_url: string;
  gravatar_id: string;
  type: string;
  site_admin: boolean;
  url: string;
  events_url: string;
  following_url: string;
  followers_url: string;
  gists_url: string;
  organizations_url: string;
  received_events_url: string;
  repos_url: string;
  starred_url: string;
  subscriptions_url: string;
}
export interface Permissions {
  admin: boolean;
  maintain: boolean;
  pull: boolean;
  push: boolean;
  triage: boolean;
}

export enum Visibility {
  Private = "private",
  Public = "public"
}

export type GithubAppErrorCode =
  | "invalid_state"
  | "exchange_failed"
  | "duplicate_installation"
  | "invalid_installation";
